import createDOMPurify, { type DOMPurify as DomPurifyInstance } from 'dompurify';

let purifyInstance: DomPurifyInstance | null = null;

function getPurify(): DomPurifyInstance {
  if (purifyInstance) return purifyInstance;
  if (typeof window === 'undefined') {
    throw new Error('DOMPurify requires a browser DOM');
  }
  purifyInstance = createDOMPurify(window);
  purifyInstance.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'src' && data.attrName !== 'href' && data.attrName !== 'xlink:href') {
      return;
    }
    const value = String(data.attrValue ?? '').trim().toLowerCase();
    if (value.startsWith('data:') && !value.startsWith('data:image/')) {
      data.keepAttr = false;
    }
  });
  return purifyInstance;
}

export interface EmailAttachment {
  fileName: string;
  contentType: string;
  bytes: Uint8Array;
  contentId?: string;
  /** True when Content-Disposition is attachment (never inlined into HTML). */
  isDispositionAttachment?: boolean;
}

export interface ParsedEmail {
  headers: Record<string, string>;
  bodyHtml: string | null;
  bodyText: string | null;
  attachments: EmailAttachment[];
}

export interface PreparedEmailHtml {
  /** HTML after sanitize + CID inline; safe base for remote-image toggling. */
  html: string;
  attachments: EmailAttachment[];
}

export interface BlockRemoteImagesResult {
  html: string;
  blockedCount: number;
}

const INLINE_IMAGE_MIMES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/svg+xml',
]);

const EMAIL_BODY_STYLE = '<style>img { max-width: 100%; height: auto; }</style>';

export function isOutlookMsgMagic(header: Uint8Array): boolean {
  return (
    header.length >= 8
    && header[0] === 0xd0
    && header[1] === 0xcf
    && header[2] === 0x11
    && header[3] === 0xe0
    && header[4] === 0xa1
    && header[5] === 0xb1
    && header[6] === 0x1a
    && header[7] === 0xe1
  );
}

export function parseEmail(bytes: Uint8Array): ParsedEmail {
  const raw = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  const { headers, body } = splitHeadersBody(raw);
  const normalized = normalizeHeaders(headers);
  const contentType = normalized['content-type'] ?? 'text/plain';
  const parsed = parseMimeBody(body, contentType);
  return {
    headers: normalized,
    bodyHtml: parsed.bodyHtml,
    bodyText: parsed.bodyText,
    attachments: parsed.attachments,
  };
}

export function prepareEmailHtml(bodyHtml: string, attachments: EmailAttachment[]): PreparedEmailHtml | null {
  const sanitized = hardenEmailLinks(sanitizeEmailHtml(bodyHtml));
  const inlined = inlineCidImages(sanitized, attachments);
  return {
    html: wrapEmailHtmlForIframe(inlined.html),
    attachments: inlined.attachments,
  };
}

export function blockRemoteImages(html: string, block: boolean): BlockRemoteImagesResult {
  if (!block) {
    return { html, blockedCount: countRemoteImageUrls(html) };
  }

  let blockedCount = 0;

  const stripRemote = (url: string): string => {
    if (!isRemoteUrl(url)) return url;
    blockedCount++;
    return '';
  };

  let out = html;

  out = out.replace(
    /(<img\b[^>]*\bsrc\s*=\s*)(['"])([^'"]*)\2/gi,
    (match, prefix, quote, url) => {
      if (!isRemoteUrl(url)) return match;
      blockedCount++;
      return `${prefix}${quote}${quote}`;
    },
  );

  out = out.replace(
    /(<img\b[^>]*\bsrcset\s*=\s*)(['"])([^'"]*)\2/gi,
    (match, prefix, quote, srcset) => {
      const filtered = filterSrcset(srcset, stripRemote);
      if (filtered.removed > 0) {
        blockedCount += filtered.removed;
        return `${prefix}${quote}${filtered.value}${quote}`;
      }
      return match;
    },
  );

  out = out.replace(
    /(<input\b[^>]*\bsrc\s*=\s*)(['"])([^'"]*)\2/gi,
    (match, prefix, quote, url) => {
      if (!isRemoteUrl(url)) return match;
      blockedCount++;
      return `${prefix}${quote}${quote}`;
    },
  );

  out = out.replace(
    /(<video\b[^>]*\bposter\s*=\s*)(['"])([^'"]*)\2/gi,
    (match, prefix, quote, url) => {
      if (!isRemoteUrl(url)) return match;
      blockedCount++;
      return `${prefix}${quote}${quote}`;
    },
  );

  out = out.replace(
    /(<a\b[^>]*\bhref\s*=\s*)(['"])([^'"]*)\2/gi,
    (match, prefix, quote, url) => {
      if (!isRemoteImageUrl(url)) return match;
      blockedCount++;
      return `${prefix}${quote}${quote}`;
    },
  );

  out = out.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (match, _quote, url) => {
    if (!isRemoteUrl(url.trim())) return match;
    blockedCount++;
    return 'url()';
  });

  return { html: out, blockedCount };
}

export function wrapEmailHtmlForIframe(html: string): string {
  if (html.includes('<head') || html.includes('<html')) {
    if (html.includes('<head')) {
      return html.replace(/<head(\s[^>]*)?>/i, (m) => `${m}${EMAIL_BODY_STYLE}`);
    }
    return html.replace(/<html(\s[^>]*)?>/i, (m) => `${m}<head>${EMAIL_BODY_STYLE}</head>`);
  }
  return `${EMAIL_BODY_STYLE}${html}`;
}

function splitHeadersBody(raw: string): { headers: string; body: string } {
  const match = raw.match(/\r?\n\r?\n/);
  if (!match || match.index == null) {
    return { headers: raw, body: '' };
  }
  return {
    headers: raw.slice(0, match.index),
    body: raw.slice(match.index + match[0].length),
  };
}

function normalizeHeaders(headers: string): Record<string, string> {
  const unfolded = headers.replace(/\r?\n[ \t]+/g, ' ');
  const out: Record<string, string> = {};
  for (const line of unfolded.split(/\r?\n/)) {
    const idx = line.indexOf(':');
    if (idx <= 0) continue;
    const key = line.slice(0, idx).trim().toLowerCase();
    const value = line.slice(idx + 1).trim();
    out[key] = out[key] ? `${out[key]}, ${value}` : value;
  }
  return out;
}

function parseContentType(value: string): { mime: string; boundary: string | null; charset: string | null } {
  const parts = value.split(';').map((p) => p.trim());
  const mime = (parts[0] ?? 'text/plain').toLowerCase();
  let boundary: string | null = null;
  let charset: string | null = null;
  for (const part of parts.slice(1)) {
    const [k, v] = part.split('=').map((s) => s.trim());
    if (!k || v == null) continue;
    const key = k.toLowerCase();
    const cleaned = v.replace(/^"(.*)"$/, '$1');
    if (key === 'boundary') boundary = cleaned;
    if (key === 'charset') charset = cleaned;
  }
  return { mime, boundary, charset };
}

function parseMimeBody(
  body: string,
  contentTypeHeader: string,
): { bodyHtml: string | null; bodyText: string | null; attachments: EmailAttachment[] } {
  const { mime, boundary } = parseContentType(contentTypeHeader);
  if (boundary) {
    return parseMultipart(body, boundary);
  }
  const decoded = decodePart(body, contentTypeHeader);
  if (mime.includes('html')) return { bodyHtml: decoded, bodyText: null, attachments: [] };
  return { bodyHtml: null, bodyText: decoded, attachments: [] };
}

function parseMultipart(
  body: string,
  boundary: string,
): { bodyHtml: string | null; bodyText: string | null; attachments: EmailAttachment[] } {
  const delimiter = `--${boundary}`;
  const parts = body.split(delimiter).map((p) => p.replace(/^\r?\n/, '').replace(/\r?\n$/, ''));
  let bodyHtml: string | null = null;
  let bodyText: string | null = null;
  const attachments: EmailAttachment[] = [];

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed || trimmed === '--') continue;
    const split = part.match(/\r?\n\r?\n/);
    if (!split || split.index == null) continue;
    const headers = normalizeHeaders(part.slice(0, split.index));
    const payload = part.slice(split.index + split[0].length);
    const contentType = headers['content-type'] ?? 'text/plain';
    const { mime, boundary: nestedBoundary } = parseContentType(contentType);

    if (nestedBoundary) {
      const nested = parseMultipart(payload, nestedBoundary);
      bodyHtml ??= nested.bodyHtml;
      bodyText ??= nested.bodyText;
      attachments.push(...nested.attachments);
      continue;
    }

    const disposition = headers['content-disposition'] ?? '';
    const dispositionLower = disposition.toLowerCase();
    const isDispositionAttachment = dispositionLower.includes('attachment');
    const fileName = extractFileName(disposition) ?? extractFileName(contentType);
    const contentId = extractContentId(headers['content-id']);
    const encoding = headers['content-transfer-encoding'];
    const decoded = decodePart(payload, contentType, encoding);

    const isBinaryPart =
      isDispositionAttachment
      || fileName != null
      || contentId != null
      || !mime.includes('html')
      && !mime.includes('plain')
      && mime.startsWith('text/') === false;

    if (isBinaryPart) {
      const name = fileName ?? contentId ?? 'attachment';
      attachments.push({
        fileName: name,
        contentType: mime,
        bytes: encoding ? decodeBinaryPart(payload, encoding) : new TextEncoder().encode(decoded),
        contentId,
        isDispositionAttachment,
      });
      continue;
    }

    if (mime.includes('html')) {
      bodyHtml = decoded;
    } else if (mime.includes('plain') || mime.startsWith('text/')) {
      bodyText = decoded;
    }
  }

  return { bodyHtml, bodyText, attachments };
}

function extractFileName(value: string): string | null {
  const match = value.match(/filename\*?=(?:UTF-8''|")?([^";\r\n]+)/i);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1].replace(/"/g, '').trim());
  } catch {
    return match[1].replace(/"/g, '').trim();
  }
}

function extractContentId(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim().replace(/^<|>$/g, '').trim();
  return trimmed || undefined;
}

function decodePart(body: string, _contentType: string, transferEncoding?: string): string {
  const encoding = (transferEncoding ?? '').toLowerCase();
  if (encoding.includes('base64')) {
    const cleaned = body.replace(/\s+/g, '');
    try {
      const binary = atob(cleaned);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    } catch {
      return body;
    }
  }
  if (encoding.includes('quoted-printable')) {
    return decodeQuotedPrintable(body);
  }
  return body;
}

function decodeBinaryPart(body: string, transferEncoding?: string): Uint8Array {
  const encoding = (transferEncoding ?? '').toLowerCase();
  if (encoding.includes('base64')) {
    const cleaned = body.replace(/\s+/g, '');
    const binary = atob(cleaned);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }
  return new TextEncoder().encode(decodeQuotedPrintable(body));
}

function decodeQuotedPrintable(input: string): string {
  const normalized = input.replace(/=\r?\n/g, '');
  const bytes: number[] = [];
  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized[i];
    if (ch === '=' && i + 2 < normalized.length) {
      const hex = normalized.slice(i + 1, i + 3);
      if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
        bytes.push(Number.parseInt(hex, 16));
        i += 2;
        continue;
      }
    }
    bytes.push(normalized.charCodeAt(i));
  }
  return new TextDecoder('utf-8', { fatal: false }).decode(new Uint8Array(bytes));
}

export function sanitizeEmailHtml(html: string): string {
  return getPurify().sanitize(html, {
    FORBID_TAGS: ['script', 'base', 'object', 'embed', 'link', 'meta', 'iframe', 'form'],
    FORBID_ATTR: ['srcdoc'],
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });
}

/** Force links to open in a new tab with noopener/noreferrer. */
export function hardenEmailLinks(html: string): string {
  return html.replace(/<(a|area)\b([^>]*)>/gi, (_match, tag: string, attrs: string) => {
    let next = attrs
      .replace(/\s+target\s*=\s*(['"][^'"]*['"]|[^\s>]+)/gi, '')
      .replace(/\s+rel\s*=\s*(['"][^'"]*['"]|[^\s>]+)/gi, '');
    return `<${tag}${next} target="_blank" rel="noopener noreferrer">`;
  });
}

function inlineCidImages(
  html: string,
  attachments: EmailAttachment[],
): { html: string; attachments: EmailAttachment[] } {
  const cidMap = new Map<string, EmailAttachment>();
  for (const attachment of attachments) {
    if (!attachment.contentId) continue;
    cidMap.set(normalizeCid(attachment.contentId), attachment);
    cidMap.set(normalizeCid(`<${attachment.contentId}>`), attachment);
  }

  const inlinedIds = new Set<EmailAttachment>();
  let out = html;

  const replaceCidRef = (cidValue: string): string | null => {
    const normalized = normalizeCid(cidValue);
    const attachment = cidMap.get(normalized);
    if (!attachment || attachment.isDispositionAttachment) return null;
    if (!INLINE_IMAGE_MIMES.has(attachment.contentType.toLowerCase())) return null;
    inlinedIds.add(attachment);
    return `data:${attachment.contentType};base64,${bytesToBase64(attachment.bytes)}`;
  };

  out = out.replace(
    /(\b(?:src|href)\s*=\s*)(['"])(cid:[^'"]+)\2/gi,
    (match, prefix, quote, url) => {
      const cidValue = url.slice(4);
      const dataUrl = replaceCidRef(cidValue);
      return dataUrl ? `${prefix}${quote}${dataUrl}${quote}` : match;
    },
  );

  out = out.replace(
    /(\bsrcset\s*=\s*)(['"])([^'"]+)\2/gi,
    (_match, prefix: string, quote: string, srcset: string) => {
      const parts = srcset.split(',').map((part: string) => part.trim()).filter(Boolean);
      const rewritten = parts.map((part: string) => {
        const spaceIdx = part.search(/\s/);
        const url = spaceIdx >= 0 ? part.slice(0, spaceIdx) : part;
        const descriptor = spaceIdx >= 0 ? part.slice(spaceIdx) : '';
        if (!url.toLowerCase().startsWith('cid:')) return part;
        const dataUrl = replaceCidRef(url.slice(4));
        return dataUrl ? `${dataUrl}${descriptor}` : part;
      });
      return `${prefix}${quote}${rewritten.join(', ')}${quote}`;
    },
  );

  const remaining = attachments.filter((attachment) => {
    if (attachment.isDispositionAttachment) return true;
    if (inlinedIds.has(attachment)) return false;
    return true;
  });

  return { html: out, attachments: remaining };
}

function normalizeCid(value: string): string {
  return value.trim().replace(/^<|>$/g, '').toLowerCase();
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function isRemoteUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (trimmed.toLowerCase().startsWith('cid:') || trimmed.toLowerCase().startsWith('data:')) return false;
  return /^https?:\/\//i.test(trimmed) || trimmed.startsWith('//');
}

function isRemoteImageUrl(url: string): boolean {
  if (!isRemoteUrl(url)) return false;
  const lower = url.trim().toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg|ico)(\?|#|$)/i.test(lower) || lower.includes('image');
}

function countRemoteImageUrls(html: string): number {
  let count = 0;
  const countRemote = (url: string) => {
    if (isRemoteUrl(url)) count++;
  };

  html.replace(/<img\b[^>]*\bsrc\s*=\s*(['"])([^'"]*)\1/gi, (_, __, url) => {
    countRemote(url);
    return '';
  });
  html.replace(/<img\b[^>]*\bsrcset\s*=\s*(['"])([^'"]*)\1/gi, (_, __, srcset) => {
    filterSrcset(srcset, (url) => {
      if (isRemoteUrl(url)) count++;
      return url;
    });
    return '';
  });
  html.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (_, __, url) => {
    countRemote(url.trim());
    return '';
  });

  return count;
}

function filterSrcset(
  srcset: string,
  transform: (url: string) => string,
): { value: string; removed: number } {
  let removed = 0;
  const parts = srcset.split(',').map((part) => part.trim()).filter(Boolean);
  const rewritten: string[] = [];
  for (const part of parts) {
    const spaceIdx = part.search(/\s/);
    const url = spaceIdx >= 0 ? part.slice(0, spaceIdx) : part;
    const descriptor = spaceIdx >= 0 ? part.slice(spaceIdx) : '';
    const nextUrl = transform(url);
    if (nextUrl !== url && nextUrl === '') {
      removed++;
      continue;
    }
    rewritten.push(descriptor ? `${nextUrl}${descriptor}` : nextUrl);
  }
  return { value: rewritten.join(', '), removed };
}
